import { z } from "zod";
import { photoUrl, storableText } from "./appointments";
import { partyKind, recordId } from "./contracts";
import { blankAsNull } from "./fields";
import { photoContentType } from "./photo-types";

export const SETTINGS_NAME_MAX_LENGTH = 200;
export const SETTINGS_DESCRIPTION_MAX_LENGTH = 2000;
export const SETTINGS_EMAIL_MAX_LENGTH = 320;
export const SETTINGS_LINK_MAX_LENGTH = 2048;
export const SETTINGS_SHORT_TEXT_MAX_LENGTH = 200;

const WEB_PROTOCOLS = new Set(["http:", "https:"]);

export const isWebAddress = (value: string): boolean => {
  try {
    return WEB_PROTOCOLS.has(new URL(value).protocol);
  } catch {
    return false;
  }
};

export const settingsName = storableText(SETTINGS_NAME_MAX_LENGTH);

export const emailAddress = z.string().trim().max(SETTINGS_EMAIL_MAX_LENGTH).email();

const webAddress = z.string().trim().max(SETTINGS_LINK_MAX_LENGTH).refine(isWebAddress, "an http or https address");

const clearable = <T extends z.ZodTypeAny>(schema: T) => z.preprocess(blankAsNull, schema.nullable()).optional();

export const clearableText = (maxLength: number) => clearable(storableText(maxLength));

export const clearableEmail = clearable(emailAddress);

export const clearableWebAddress = clearable(webAddress);

export const organizationSettings = z.object({
  id: recordId,
  kind: partyKind,
  name: z.string(),
  description: z.string().nullable(),
  supportEmail: z.string().nullable(),
  billingEmail: z.string().nullable(),
  website: z.string().nullable(),
  phone: z.string().nullable(),
  location: z.string().nullable(),
  timezone: z.string().nullable(),
  logo: z.string().nullable(),
});
export type OrganizationSettings = z.infer<typeof organizationSettings>;

export const organizationSettingsRequest = z
  .object({
    name: settingsName,
    supportEmail: emailAddress,
    description: clearableText(SETTINGS_DESCRIPTION_MAX_LENGTH),
    billingEmail: clearableEmail,
    website: clearableWebAddress,
    phone: clearableText(SETTINGS_SHORT_TEXT_MAX_LENGTH),
    location: clearableText(SETTINGS_SHORT_TEXT_MAX_LENGTH),
    timezone: clearableText(SETTINGS_SHORT_TEXT_MAX_LENGTH),
  })
  .strict();
export type OrganizationSettingsRequest = {
  name: string;
  supportEmail: string;
  description?: string | null;
  billingEmail?: string | null;
  website?: string | null;
  phone?: string | null;
  location?: string | null;
  timezone?: string | null;
};

export const imageUploadRequest = z.object({ contentType: photoContentType }).strict();
export type ImageUploadRequest = z.infer<typeof imageUploadRequest>;

export const organizationLogoRequest = z.object({ logoUrl: photoUrl }).strict();
export type OrganizationLogoRequest = z.infer<typeof organizationLogoRequest>;
